export type SoundEffect = "select" | "correct" | "wrong" | "finish";

type AudioContextWindow = Window & {
  webkitAudioContext?: typeof AudioContext;
};

interface Tone {
  readonly frequency: number;
  readonly duration: number;
  readonly delay?: number;
  readonly type?: OscillatorType;
  readonly volume?: number;
}

export class AudioManager {
  private context: AudioContext | null = null;

  public constructor(private enabled: boolean) {}

  public isEnabled(): boolean {
    return this.enabled;
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  public play(effect: SoundEffect): void {
    if (!this.enabled) return;
    const toneMap: Readonly<Record<SoundEffect, readonly Tone[]>> = {
      select: [{ frequency: 390, duration: 0.07, volume: 0.045 }],
      wrong: [{ frequency: 185, duration: 0.16, type: "triangle", volume: 0.08 }],
      correct: [
        { frequency: 560, duration: 0.15, volume: 0.09 },
        { frequency: 760, duration: 0.2, delay: 0.09, volume: 0.09 },
      ],
      finish: [
        { frequency: 523, duration: 0.2, volume: 0.1 },
        { frequency: 659, duration: 0.2, delay: 0.13, volume: 0.1 },
        { frequency: 784, duration: 0.34, delay: 0.26, volume: 0.1 },
      ],
    };
    const tones = toneMap[effect];

    const context = this.getContext();
    if (!context) return;
    if (context.state === "suspended") void context.resume();
    tones.forEach((tone) => this.playTone(context, tone));
  }

  private getContext(): AudioContext | null {
    if (this.context) return this.context;
    const AudioContextClass = window.AudioContext ??
      (window as AudioContextWindow).webkitAudioContext;
    if (!AudioContextClass) return null;
    this.context = new AudioContextClass();
    return this.context;
  }

  private playTone(context: AudioContext, tone: Tone): void {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const start = context.currentTime + (tone.delay ?? 0);
    const volume = tone.volume ?? 0.08;

    oscillator.type = tone.type ?? "sine";
    oscillator.frequency.setValueAtTime(tone.frequency, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + tone.duration);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(start);
    oscillator.stop(start + tone.duration + 0.02);
  }
}
